import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCloseModal } from '@/hooks/useCloseModal';
import { isDialogSubmitKey } from '@/util/events';

export type FormDialogProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitDisabled?: boolean;
  cancelDisabled?: boolean;
  isLoading?: boolean;
  preventClose?: boolean;
  contentClassName?: string;
  /**
   * Fire the submit action on Enter, matching the legacy Modal's `autoFocus` behaviour.
   * Opt-in, so dialogs where Enter means something else are left alone.
   */
  submitOnEnter?: boolean;
  /** Render the submit button as destructive. */
  danger?: boolean;
  /**
   * Open state, for dialogs mounted locally by their page. Omit it when rendering through
   * the Redux ModalConductor, which mounts the component only while the modal is open —
   * dismissal then falls back to popping the modal stack.
   */
  open?: boolean;
  /** Dismiss callback, required alongside `open`. */
  onOpenChange?: (open: boolean) => void;
};

const FormDialog = ({
  title,
  description,
  children,
  submitLabel,
  cancelLabel,
  onSubmit,
  onCancel,
  submitDisabled,
  cancelDisabled,
  isLoading,
  preventClose,
  contentClassName,
  submitOnEnter = false,
  danger = false,
  open,
  onOpenChange,
}: FormDialogProps) => {
  const { t } = useTranslation();
  const closeModal = useCloseModal();

  const close = open === undefined ? closeModal : () => onOpenChange?.(false);
  const handleCancel = onCancel ?? close;

  return (
    <Dialog
      open={open ?? true}
      onOpenChange={(nextOpen) => !nextOpen && !preventClose && handleCancel()}
    >
      <DialogContent
        className={contentClassName}
        showCloseButton={!preventClose}
        onPointerDownOutside={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
        onKeyDown={(event) => {
          if (!submitOnEnter || submitDisabled || isLoading || !isDialogSubmitKey(event)) {
            return;
          }
          event.preventDefault();
          onSubmit?.();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={handleCancel} disabled={cancelDisabled}>
            {cancelLabel || t('common.cancel')}
          </Button>
          <Button
            variant={danger ? 'destructive' : 'primary'}
            onClick={onSubmit}
            disabled={submitDisabled}
            isLoading={isLoading}
          >
            {submitLabel || t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FormDialog;
